<script setup lang="ts">
import { onMounted } from 'vue';
import { PERMISSIONS } from '@/enum';
import { can, useDateTime } from '@/composables';
import { useArchiveStore } from '@/store';

const archiveStore = useArchiveStore();

interface Menu {
  label: string;
  icon: {
    name: string;
    styles: string;
  };
  styles: string;
  command: () => void;
  visible: boolean;
}

const createMenuModel = (item: Menu) => {
  return [
    {
      label: 'Archive',
      icon: {
        name: 'vpn_key',
        styles: 'inline-block text-lg',
      },
      styles: '',
      command: () => onArchive(item.person_id, item.person_name, item.archive_reason_id),
      visible: can([PERMISSIONS.PEOPLE_ARCHIVE]),
    },
    {
      label: 'Keep',
      icon: {
        name: 'cancel',
        styles: 'inline-block text-lg',
      },
      styles: '',
      command: () => onKeep(item.person_id),
      visible: can([PERMISSIONS.PEOPLE_ARCHIVE_KEEP]),
    },
  ];
};

onMounted(async () => init());

const init = async () => await archiveStore.toBeArchived();

const onArchive = async (personId: number, person_name: string, archiveReasonId: number) => {
  await archiveStore.archive(personId, archiveReasonId ? { archived_reason_id: archiveReasonId } : {});
  await archiveStore.toBeArchived();
  archiveStore.doToast(`${person_name} has been archived`, 'Archived');
};

const onKeep = async (personId: number) => await archiveStore.archiveKeep(personId);
</script>

<template>
  <div class="page-content">
    <Card>
      <template #content>
        <DataTable
          :value="archiveStore.peopleToBeArchived"
          :loading="archiveStore.peopleToBeArchivedLoading"
          data-key="student_id"
          paginator
          :rows="archiveStore.peopleToBeArchivedTotal"
          :rows-per-page-options="[15, 25, 30]"
          paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          current-page-report-template="Showing {first} to {last} of {totalRecords}"
          responsive-layout="scroll"
        >
          <template #empty>No users to be archived found</template>
          <Column field="name" header="Name">
            <template #body="{ data }">
              <div class="justify-content-center">
                <RouterLink
                  :to="`/user/${data.person_id}`"
                  target="_blank"
                  class="font-semibold"
                  v-text="data.person_name"
                />
              </div>
            </template>
          </Column>
          <Column field="organization" header="Organization">
            <template #body="{ data }">
              <template v-if="data.organizations.length > 0">
                <template v-for="(organization, key) in data.organizations" :key="key">
                  <RouterLink
                    :to="`/organizations/${organization.organization_id}`"
                    target="_blank"
                    class="font-semibold"
                    v-text="organization.organization_name"
                  />
                  <span
                    v-if="data.organizations.length > 0 && key !== data.organizations.length - 1"
                    class="mx-1"
                    v-text="'/'"
                  />
                </template>
              </template>
              <template v-else>
                <span v-text="'N/A'" />
              </template>
            </template>
          </Column>
          <Column field="person_type_title" header="Type" />
          <Column field="archive_at" header="To Be Archived">
            <template #body="{ data }">
              <span v-text="useDateTime().formatIntlDate(data.archive_at)" />
            </template>
          </Column>
          <Column field="actions" header="Actions" header-style="max-width: 50px;" body-style="max-width: 50px;">
            <template #body="{ data }">
              <ActionMenu class="ml-3" :menu-items="createMenuModel(data)" icon="more_horiz" />
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  padding: 0px;

  .p-datatable {
    margin: 0 !important;

    .p-datatable-tbody {
      tr {
        td {
          .p-dropdown {
            height: 1.8rem;
          }
        }
      }
    }
  }

  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }
}
</style>
